/**
 * @file FILEPATH: https://www.figma.com/design/UjwnEKRIXK7uLoilkWtUha/UIUX---Home-Page--%40Tomi?node-id=2170-417&m=dev
 */

import {
  Accessor,
  JSX,
  children,
  createEffect,
  createMemo,
  createSignal,
  mergeProps,
  onCleanup,
  onMount,
  splitProps,
  untrack,
} from 'solid-js';
import { mergeRefs } from '@solid-primitives/refs';
import { ReactiveSet } from '@solid-primitives/set';

import Scrollbar from './Scrollbar';
import { cx } from '../utils';
import Button from './Button';
import { useConfig } from './Config';
import LazyList from './LazyList';

export interface SmoothListBaseProps<T> {
  /**
   * Whether to show the progress bar.
   */
  progressBar?: boolean;

  /**
   * The data array to be rendered in the swiper.
   */
  data: T[];
}

const smoothListSet = new ReactiveSet<HTMLDivElement>();

export interface SmoothListProps<T>
  extends Omit<JSX.HTMLAttributes<HTMLDivElement>, 'children'>, SmoothListBaseProps<T> {
  /**
   * The component to render for each item in the data array.
   */
  children: (data: Accessor<T | undefined>, index: Accessor<number>) => JSX.Element;
}

/**
 * A swiper component based on native snap scrolling alignment.
 *
 *
 * @template T - The type of data for each slide.
 * @param {SmoothListProps<T>} props - The props for the swiper component.
 * @returns {JSX.Element} - The rendered swiper component.
 */
export default function SmoothList<T>(props: SmoothListProps<T>) {
  const [pps, others] = splitProps(props, ['data', 'progressBar']);

  const localProps = mergeProps(
    {
      progressBar: false,
      data: [],
    },
    pps
  );

  let containerRef: HTMLDivElement;

  const [data, setData] = createSignal<any[]>([]);
  createEffect(() => setData(localProps.data || []));

  const scrollProps = Scrollbar.createScrollProps(() => containerRef);

  const childrens = children(() => (
    <LazyList
      each={data()}
      children={(...args) => {
        return <div>{props.children(...args)}</div>;
      }}
    />
  ));

  const container = children(() => (
    <div
      {...others}
      ref={mergeRefs((r) => (containerRef = r), others.ref)}
      class={cx(
        'grid smooth-scroll smooth-list snap-x relative snap-mandatory grid-flow-col overflow-x-scroll overflow-y-hidden scroll-smooth hide-scroll',
        props.class
      )}
    >
      {childrens()}
    </div>
  ));

  const getProgressBarProps = () => {
    const totalScrollableWidth = scrollProps.scrollWidth - scrollProps.clientWidth;
    const currentScrollPercentage = scrollProps.scrollLeft / totalScrollableWidth;

    const barLengthRatio = scrollProps.clientWidth / scrollProps.scrollWidth;

    const progress = (currentScrollPercentage * (1 - barLengthRatio)) / barLengthRatio;

    return {
      progress,
      barLengthRatio,
    };
  };

  onMount(() => {
    smoothListSet.add(containerRef);
    onCleanup(() => {
      smoothListSet.delete(containerRef);
    });
  });

  return (
    <>
      {container()}
      <Toggle when={localProps.progressBar}>
        <SwiperProgressbar {...getProgressBarProps()} />
      </Toggle>
    </>
  );
}

const SwiperProgressbar = (props: { progress: number; barLengthRatio: number }) => {
  return (
    <div class={cx('m-auto mt-2 h-1 w-20 overflow-hidden rounded-sm bg-layer4')}>
      <div
        class="h-full rounded bg-alw_white"
        style={{
          transform: `translateX(${100 * props.progress}%)`,
          width: `${100 * props.barLengthRatio}%`,
        }}
      ></div>
    </div>
  );
};

interface ISmoothListControllerProps {
  smoothListSelector?: () => HTMLDivElement;
  children?: (props: {
    leftButtonProps: {
      disabled: boolean;
      onClick: () => void;
    };
    rightButtonProps: {
      disabled: boolean;
      onClick: () => void;
    };
  }) => JSX.Element;
}

export const SmoothListController = (
  _props: ISmoothListControllerProps & Omit<JSX.DetailsHtmlAttributes<HTMLDivElement>, 'children'>
) => {
  const { icons } = useConfig();
  const [controller, setController] = createSignal<HTMLDivElement | null>(null);
  const props = mergeProps(
    {
      smoothListSelector: () => {
        if (!controller()) return null;
        // 默认找最近的smoothList
        let nearestElement = null;
        let minDistance = Infinity;

        smoothListSet.forEach((element) => {
          if (element === controller()) return; // Skip the element itself

          // 视觉上垂直方向的最近
          const distance = getDistance(controller()!, element);
          if (distance.dy < minDistance) {
            minDistance = distance.dy;
            nearestElement = element;
          }
        });

        return nearestElement;
      },
    },
    _props
  );

  const [_, restProps] = splitProps(props, ['smoothListSelector']);

  const smoothList = createMemo(() => {
    return props.smoothListSelector();
  });

  const scrollProps = Scrollbar.createScrollProps(() => smoothList()!);

  const perPage = createMemo(() => {
    return scrollProps.clientWidth;
  });

  const createDirection = (direction: -1 | 1) => {
    const disabled = createMemo(() => {
      return direction === -1
        ? scrollProps.scrollLeft === 0
        : scrollProps.scrollWidth - scrollProps.clientWidth - scrollProps.scrollLeft <= 1;
    });

    const onClick = () => {
      untrack(() => {
        if (scrollProps.isScrolling) return;
        // 不用scrollBy,safari scrollBy 有点问题
        smoothList()?.scrollTo({
          left: scrollProps.scrollLeft + direction * perPage(),
          top: 0,
          behavior: 'smooth',
        });
      });
    };

    return {
      disabled,
      onClick,
    };
  };

  const leftProps = createMemo(() => {
    return createDirection(-1);
  });

  const rightProps = createMemo(() => createDirection(1));

  const _children = children(() => {
    if (props.children) {
      return props.children({
        leftButtonProps: {
          ...leftProps(),
          disabled: leftProps().disabled(),
        },
        rightButtonProps: {
          ...rightProps(),
          disabled: rightProps().disabled(),
        },
      });
    }
    return (
      <>
        <Button
          disabled={leftProps().disabled()}
          onClick={leftProps().onClick}
          type="second"
          class="size-8 bg-layer5-card"
        >
          <icons.arrow />
        </Button>
        <Button
          disabled={rightProps().disabled()}
          onClick={rightProps().onClick}
          type="second"
          class="size-8 bg-layer5-card"
        >
          <icons.arrow class="rotate-180" />
        </Button>
      </>
    );
  });
  return (
    <div {...restProps} class={cx(restProps.class, 'flex gap-x-1')} ref={setController}>
      {_children()}
    </div>
  );
};

const getDistance = (ele1: HTMLElement, ele2: HTMLElement) => {
  const rect1 = ele1.getBoundingClientRect();
  const rect2 = ele2.getBoundingClientRect();
  const dx = Math.max(0, Math.min(Math.abs(rect1.left - rect2.right), Math.abs(rect2.left - rect1.right)));
  const dy = Math.max(0, Math.min(Math.abs(rect1.top - rect2.bottom), Math.abs(rect2.top - rect1.bottom)));

  return { sqrt: Math.sqrt(dx * dx + dy * dy), dx, dy, rect1, rect2 };

  // const commonAncestor = findLowestCommonAncestor(ele1, ele2);
  // function getDepth(element: HTMLElement, ancestor: HTMLElement) {
  //   let depth = 0;
  //   while (element !== ancestor) {
  //     element = element.parentElement!;
  //     depth++;
  //   }
  //   return depth;
  // }
  // const depth1 = getDepth(ele1, commonAncestor);
  // const depth2 = getDepth(ele2, commonAncestor);
  // return depth1 + depth2;
};
